import getTaxRateFromDefaultLockingComponent from "./getTaxRateFromDefaultLockingComponent"
import getLockingSystemComponentFromLockingSystemProduct from "./getLockingSystemComponentFromLockingSystemProduct"
import getLockingSystemPricePortions, {
	getLockingSystemMsrpPricePortions,
} from "./getLockingSystemPricePortions"
import getReducerStateWithLockingSystemCharges from "./getReducerStateWithLockingSystemCharges"

import { CountryCode } from "@secureo/common/typings/CountryCode"
import { LockingSystemCategory } from "@secureo/common/typings/Category"
import { LockingSystemReducerState } from ".."
import { LockingSystemMatrixType } from "@secureo/common/typings/LockingSystem"

export const getInitialLockingSystemComponents = (
	lockingSystemCategory: LockingSystemCategory,
	countryCode: CountryCode,
	taxRate: number,
) => {
	const { defaultLockingComponentSku, defaultKeyComponentSku, lockingSystemType } =
		lockingSystemCategory

	const componentsByUuid: LockingSystemReducerState["componentsByUuid"] = {}
	const lockingComponentsUuids: LockingSystemReducerState["lockingComponentsUuids"] = []
	let defaultLockingComponent: LockingSystemReducerState["defaultLockingComponent"] = null
	const keyComponentsUuids: LockingSystemReducerState["keyComponentsUuids"] = []
	let defaultKeyComponent: LockingSystemReducerState["defaultKeyComponent"] = null

	const matrixType: LockingSystemMatrixType = "single"
	const matrix: LockingSystemReducerState["matrix"] = [[true]]

	if (defaultLockingComponentSku) {
		const initialLockingComponent = getLockingSystemComponentFromLockingSystemProduct(
			lockingSystemCategory.defaultLockingComponent,
			"lock",
			countryCode,
			taxRate,
		)
		const { uuid } = initialLockingComponent

		lockingComponentsUuids.push(uuid)
		componentsByUuid[uuid] = initialLockingComponent
		defaultLockingComponent = { ...initialLockingComponent }
	}

	if (defaultKeyComponentSku) {
		const initialKeyComponent = getLockingSystemComponentFromLockingSystemProduct(
			lockingSystemCategory.defaultKeyComponent,
			"key",
			countryCode,
			taxRate,
		)
		const { uuid } = initialKeyComponent

		keyComponentsUuids.push(uuid)
		componentsByUuid[uuid] = initialKeyComponent
		defaultKeyComponent = { ...initialKeyComponent }
	}

	const defaultLockingSystemComponents: Pick<
		LockingSystemReducerState,
		| "lockingSystemType"
		| "defaultLockingComponentSku"
		| "defaultLockingComponent"
		| "defaultKeyComponentSku"
		| "defaultKeyComponent"
		| "keyComponentsUuids"
		| "lockingComponentsUuids"
		| "componentsByUuid"
		| "matrixType"
		| "matrix"
	> = {
		lockingSystemType,
		defaultLockingComponentSku,
		defaultLockingComponent,
		defaultKeyComponentSku,
		defaultKeyComponent,
		componentsByUuid,
		lockingComponentsUuids,
		keyComponentsUuids,
		matrix,
		matrixType,
	}

	return defaultLockingSystemComponents
}

const getInitialReducerState = (
	lockingSystemCategory: LockingSystemCategory,
	locale: string,
	countryCode: CountryCode,
	isDebug = false,
	isLoading = true,
) => {
	if (!lockingSystemCategory) return {}

	const taxRate = getTaxRateFromDefaultLockingComponent(
		lockingSystemCategory?.defaultLockingComponent,
		countryCode,
	)
	const {
		lockingSystemType,
		defaultLockingComponentSku,
		defaultLockingComponent,
		defaultKeyComponentSku,
		defaultKeyComponent,
		keyComponentsUuids,
		lockingComponentsUuids,
		componentsByUuid,
		matrixType,
		matrix,
	} = getInitialLockingSystemComponents(lockingSystemCategory, countryCode, taxRate)

	const { singleNetPriceCents, singleGrossPriceCents, totalNetPriceCents, totalGrossPriceCents } =
		getLockingSystemPricePortions(componentsByUuid, 1)

	const {
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
	} = getLockingSystemMsrpPricePortions(componentsByUuid, 1)

	const isQuickStartOpen = !isLoading

	const initialReducerState: LockingSystemReducerState = getReducerStateWithLockingSystemCharges({
		// UI state
		isLoading,
		isFrozen: false,
		isQuickStartOpen,

		// Locking System
		system: lockingSystemCategory.key,
		lockingSystemType,
		defaultLockingComponentSku,
		defaultLockingComponent,
		defaultKeyComponentSku,
		defaultKeyComponent,

		// Localization
		countryCode,
		locale,

		// Save / Load
		hasUnsavedChanges: false,
		shoppingListId: null,
		shoppingListVersion: null,

		// Personalization
		name: "",

		// Components
		componentsByUuid,
		lockingComponentsUuids,
		keyComponentsUuids,

		// Matrix
		matrix,
		matrixType,

		// Tax & Quantity
		taxRate,
		quantity: 1,

		// Price
		singleNetPriceCents,
		singleGrossPriceCents,
		totalNetPriceCents,
		totalGrossPriceCents,

		// Msrp price
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,

		// Debugging
		isDebug,
	})

	return initialReducerState
}

export default getInitialReducerState
